import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useGetEntityDataAttributesQuery } from "store/apis/entity";
import {
  FieldBuilder,
  FieldBuilderProps,
} from "components/Entity/EntityFormEngine/FieldBuilder";
import {
  BASIC_INFORMATION_SECTION,
  CONTACT_INFORMATION,
} from "constants/onBoarding";
import classNames from "classnames";
import { useQuery } from "hooks/useQuery";
import { ToolTipIcon } from "components/icons/TooltipIcon";
import ToolTip from "components/design/toolTip";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Button } from "components/DesignSystem/Button/Button";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import { ReactNode } from "react";
import { EntityDataAttribute } from "types/Models/entity";
import { openLink } from "utils/openLink";

const fullWidthFieldType = ["ADDRESS"];
const fullWidthFieldName = ["naics_code"];

const NaicsCodeInput = ({ field, size, onChange }: FieldBuilderProps) => {
  const {
    disabled,
    label,
    name,
    is_required: required,
    placeholder,
    tooltip,
  } = field;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.({ name, value: e.target.value });
  };

  const onFindCode = () => {
    openLink("https://www.naics.com/search/");
  };
  return (
    <div className="t-relative">
      <TextInput
        customSize={size}
        name={name}
        disabled={disabled}
        placeholder={placeholder || ""}
        label={label}
        required={required}
        onChange={handleChange}
        tooltipText={tooltip}
        block
        defaultValue={(field.value || "") as string}
      />
      <div className="t-absolute t-right-2 t-top-0">
        <Button onClick={onFindCode} customType="link" size="small">
          <span className="t-text-body-sm">Find code</span>
        </Button>
      </div>
    </div>
  );
};

const EntityDetailsItem = ({
  children,
  value,
  tooltipText,
}: {
  children: ReactNode;
  value?: string;
  tooltipText?: string;
}) => {
  return (
    <div>
      <div className="t-flex t-items-center">
        <label className={classNames("t-text-body-sm t-text-text-30 t-m-0")}>
          {children}
        </label>
        {tooltipText && (
          <div className="t-ml-1 t-pb-1.5">
            <ToolTip text={tooltipText} side="right">
              <span>
                <ToolTipIcon />
              </span>
            </ToolTip>
          </div>
        )}
      </div>

      <span className="t-text-body-sm t-text-text-100 ">{value}</span>
    </div>
  );
};

type SalesTaxEntityDetailsBaseProps = {
  onChange: ({
    name,
    value,
  }: {
    name: string;
    value: EntityDataAttribute["value"];
  }) => Promise<void>;
  children?: ReactNode;
  isDisabled?: boolean;
};

export const SalesTaxEntityDetailsBase = ({
  onChange,
  children,
  isDisabled,
}: SalesTaxEntityDetailsBaseProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const query = useQuery();
  let entityId = query.get("entity") as string;

  const { data: currentEntityDetails = [] } = useGetEntityDataAttributesQuery(
    {
      groupId: groupId!,
      entityId,
    },
    {
      skip: !groupId || !entityId,
    }
  );

  const group = useCurrentGroupContext();

  const entityDetails = useCurrentEntity();

  return (
    <div>
      <div className="t-grid t-grid-cols-2 t-gap-x-4 t-gap-y-3 t-border-neutral-10 t-pb-6 t-border-b t-border-0 t-border-solid">
        <EntityDetailsItem value={entityDetails?.name}>
          Business name
        </EntityDetailsItem>
        <div />
        <EntityDetailsItem
          value={group?.name}
          tooltipText="Group Name or Doing Business As (DBA)"
        >
          Trade name or DBA
        </EntityDetailsItem>

        <EntityDetailsItem value={entityDetails?.formation_type}>
          Entity type
        </EntityDetailsItem>

        {currentEntityDetails
          .filter(
            ({ section_name, is_sales_tax_onboarding_field, name }) =>
              section_name === BASIC_INFORMATION_SECTION &&
              is_sales_tax_onboarding_field &&
              name !== "principal_place_of_business"
          )
          .map((field) => {
            return (
              <div
                className={classNames({
                  "t-col-span-2":
                    fullWidthFieldType.includes(field.type) ||
                    fullWidthFieldName.includes(field.name),
                })}
                key={field.uuid}
              >
                {field.name === "naics_code" ? (
                  <NaicsCodeInput
                    field={{
                      ...field,
                      disabled: Boolean(field.disabled || isDisabled),
                    }}
                    onChange={onChange}
                    size="regular"
                  />
                ) : (
                  <FieldBuilder
                    field={{
                      ...field,
                      disabled: Boolean(field.disabled || isDisabled),
                    }}
                    onChange={onChange}
                    size="regular"
                    entityIdFromProps={entityId}
                  />
                )}
              </div>
            );
          })}
        {children && <div className="t-col-span-2">{children}</div>}
      </div>
      <div>
        <p className="t-text-text-60 t-text-subtitle-sm t-mt-6">
          Contact details
        </p>
        <div className="t-grid t-grid-cols-2 t-items-start t-justify-start t-gap-x-4 t-gap-y-3">
          {currentEntityDetails
            .filter(
              ({ section_name, is_sales_tax_onboarding_field, name }) =>
                (section_name === CONTACT_INFORMATION &&
                  is_sales_tax_onboarding_field) ||
                name === "principal_place_of_business"
            )
            .map((field) => {
              return (
                <div
                  className={classNames({
                    "t-col-span-2": fullWidthFieldType.includes(field.type),
                  })}
                  key={field.uuid}
                >
                  <FieldBuilder
                    field={{
                      ...field,
                      disabled: Boolean(field.disabled || isDisabled),
                    }}
                    onChange={onChange}
                    size="regular"
                  />
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};
